<template>
  <iColumn v-bind="myLayoutProps" :width="layoutWidth">
    <template v-if="editMode">
      <StudioFeedRowItem
        v-for="row in boundPreviewRows"
        :key="`preview-${row.ux}`"
        :dynamic-component-id="row.componentId"
        :entity-id="row.previewEntityId"
        :context-name="questionContextName"
        :theme-mode="themeMode"
        edit-mode
      />
      <iText v-if="boundPreviewRows.length === 0" variant="subtle" font-size="small">
        Bind a component to at least one answer input type in the inspector.
      </iText>
    </template>

    <template v-else>
      <AppComponentOverrideHost
        v-if="showsOneSection && stepProgressCardId && submissionEntityId"
        :app-name="questionnaireAppName"
        :dynamic-component-id="stepProgressCardId"
        :contexts="submissionContexts"
        :theme-mode="themeMode"
      />
      <template v-for="row in visibleRows" :key="row.id">
        <iText
          v-if="row.startsSection && row.sectionTitle && !showsOneSection && row.sectionId !== bandSectionId"
          variant="subtle"
          font-size="small"
          font-weight="dark"
          data-test="questionnaire-section"
        >
          {{ row.sectionTitle }}
        </iText>
        <AppComponentOverrideHost
          :app-name="questionnaireAppName"
          :dynamic-component-id="cardIdForRow(row)"
          :contexts="rowContexts(row)"
          :theme-mode="themeMode"
        />
      </template>
      <AppComponentOverrideHost
        v-if="showsOneSection && stepNavCardId && submissionEntityId"
        :app-name="questionnaireAppName"
        :dynamic-component-id="stepNavCardId"
        :contexts="submissionContexts"
        :theme-mode="themeMode"
      />
      <AppComponentOverrideHost
        v-if="showsSubmissionCard"
        :app-name="questionnaireAppName"
        :dynamic-component-id="submissionCardId"
        :contexts="submissionContexts"
        :theme-mode="themeMode"
      />
    </template>
  </iColumn>
</template>

<script>
import App from "@bloglovin/inspire-engine/App";
import { currentContextSubmissionId } from "+/QuizzesPolls/Components/current-submission";
import { bandSectionTitle, memberSectionTitle } from "+/QuizzesPolls/Components/section-titles";
import StudioFeedRowItem from "%/dynamic-component-editor/standard-elements/StudioFeedRowItem.vue";
import AppComponentOverrideHost
  from "%/dynamic-component-editor/standard-elements/dynamic-app-elements/display/AppComponentOverrideHost.vue";
import { DynamicElementMixin } from "%/dynamic-component-editor/mixins/dynamic-element-mixin";
import { resolveBoundEntityId, resolveNamedContextEntityId }
  from "%/dynamic-component-editor/helpers/bound-context-entity-id";
import {
  LayoutElementMixinProduction,
} from "%/dynamic-component-editor/standard-elements/layouts/layout-element-mixin-production";
import LayoutPositionOptions from "%/dynamic-component-editor/bundles/layout-position-options";
import AppearanceOptions from "%/dynamic-component-editor/bundles/appearance-options";
import SpacingOptions from "%/dynamic-component-editor/bundles/spacing-options";
import BackgroundOptions from "%/dynamic-component-editor/bundles/background-options";
import EffectsOptions from "%/dynamic-component-editor/bundles/effects-options";
import LayoutWidth from "%/dynamic-component-editor/options/layout-width";
import { declaredAppOption, declaredAppOptions, declarationsForApp }
  from "%/dynamic-component-editor/options/declared-app-options";
import { QUESTIONNAIRE_CARDS_OPTION_KEY } from "+/QuizzesPolls/Components/questionnaire-card-selection";
import { QUIZZES_POLLS_APP_NAME } from "+/QuizzesPolls/app-name";

const PACING_KEY = "question-pacing";
const PACING_SECTION = "section";
const SUBMISSION_CARD_KEY = "submission-card";
const STEP_PROGRESS_CARD_KEY = "step-progress-card";
const STEP_NAV_CARD_KEY = "step-nav-card";
const CURRENT_STEP_FIELD = "currentStep";
const UPDATE_FIELD_EVENT = "UpdateEntityField";
const STEP_STATE_FIELDS = ["stepCount",
  "stepNumber",
  "stepPercent",
  "currentSectionTitle",
  "stepOutstanding",
  "canLeaveStep",
  "onFirstStep",
  "onLastStep",
  "answeredCount"];
const SUBMITTED_CARD_KEY = "submitted-answer-card";
const SUBMISSION_STATE_FIELD = "submissionState";
const SUBMISSION_STATE_COMPLETED = "completed";
const PROMPT_FIELD = "prompt";
const RANK_FIELD = "rank";
const REQUIRED_FIELD = "required";
const UX_ELEMENT_FIELD = "uxElement";
const SECTION_FIELD = "section";
const SECTION_NAME_PATH = "section.name";
const SECTION_RANK_PATH = "section.rank";
const IS_ANSWERED_FIELD = "isAnswered";
const MY_ANSWER_FIELD = "myAnswer";
const CARD_KEY_PREFIX = "question-card-variant-";

function cardOptionKeyForUx(ux) {
  return `${CARD_KEY_PREFIX}${String(ux ?? "").replace(/_/g, "-")}`;
}

function questionCardOptionKeys() {
  return Object.keys(declarationsForApp(QUIZZES_POLLS_APP_NAME))
      .filter(key => key.startsWith(CARD_KEY_PREFIX));
}

const ANSWER_CONTEXT_NAME = "answer";
const SUBMISSION_CONTEXT_NAME = "submission";
const QUESTIONNAIRE_CONTEXT_NAME = "questionnaire";
const PARTICIPANT_ENTITY_TYPE = "Participant";
const SUBMISSIONS_FIELD = "submissions";
const START_SUBMISSION_EVENT = "StartSubmission";

const ANSWERS_FIELD = "answers";
const QUESTIONS_FIELD = "questions";
const QUESTIONNAIRE_ENTITY_TYPE = "Questionnaire";
const SECTION_TITLE_FIELD_ON_QUESTIONNAIRE = "currentSectionTitle";
const SUBMISSION_ENTITY_TYPE = "Submission";
const ANSWER_ENTITY_TYPE = "Answer";
const QUESTION_ENTITY_TYPE = "Question";
const QUESTION_CONTEXT_NAME = "question";
const QUESTION_FIELD = "question";
const DEFAULT_APP_NAME = "QuizzesPolls";

export default {
  name: "QuestionnaireElement",
  components: { StudioFeedRowItem, AppComponentOverrideHost },
  mixins: [LayoutElementMixinProduction, DynamicElementMixin],
  inject: {
    pageEntities: { default: null },
    scopedProvidedContexts: { default: null },
  },
  data() {
    return {
      isStartingSubmission: false,
    };
  },
  elementPanelSettings: {
    displayName: "Questionnaire",
    displayIcon: "mdi:clipboard-text-outline",
    elementType: "element",
    elementGroup: "general",
  },
  options: [
    ...declaredAppOptions(QUIZZES_POLLS_APP_NAME, [
      PACING_KEY,
      QUESTIONNAIRE_CARDS_OPTION_KEY,
      ...questionCardOptionKeys(),
      SUBMISSION_CARD_KEY,
      STEP_PROGRESS_CARD_KEY,
      STEP_NAV_CARD_KEY,
      SUBMITTED_CARD_KEY,
    ]),
  ],
  optionBundles: [
    new AppearanceOptions(),
    new LayoutPositionOptions({
      "default.static-layout-direction": "vertical",
    }),
    SpacingOptions,
    BackgroundOptions,
    new EffectsOptions({}, {
      "text-shadow": true,
    }),
  ],
  computed: {
    layoutWidth() {
      return this.getOptionValue(LayoutWidth) || "fill";
    },
    questionContextName() {
      return QUESTION_CONTEXT_NAME;
    },
    questionnaireEntityId() {
      return resolveBoundEntityId(this, QUESTIONNAIRE_CONTEXT_NAME) || 0;
    },
    participantPk() {
      const stamped = window.blclient?.data?.dbApps?.[this.questionnaireAppName]?.appUserPk;
      return stamped && stamped.id ? stamped : null;
    },
    resolvedSubmissionEntityId() {
      if (!this.questionnaireEntityId || !this.participantPk || !this.questionnaireApp) {
        return 0;
      }
      const items = this.questionnaireApp.getFeedItems(
        PARTICIPANT_ENTITY_TYPE,
        this.participantPk,
        SUBMISSIONS_FIELD,
      ) ?? [];
      const match = items
          .map(item => item?.entityPk ?? item?.pk ?? null)
          .filter(Boolean)
          .find(entityPk => {
            const questionnaire = this.submissionField(entityPk, "questionnaire");
            return Number(questionnaire?.pk?.id ?? questionnaire?.id ?? 0) === this.questionnaireEntityId;
          });
      return Number(match?.id) || 0;
    },
    contextSubmissionEntityId() {
      const fromContext = resolveNamedContextEntityId(this, SUBMISSION_CONTEXT_NAME) || 0;
      if (!fromContext || !this.questionnaireEntityId || !this.questionnaireApp) {
        return fromContext;
      }
      return currentContextSubmissionId(fromContext, this.questionnaireEntityId, (id, fieldName) => this.submissionField({ id }, fieldName));
    },
    submissionEntityId() {
      return this.contextSubmissionEntityId || this.resolvedSubmissionEntityId;
    },
    elementContext() {
      const elementConfig = this.dynamicComponentInstance?.getElement(this.identifier);
      return this.optionValues?.context
        || elementConfig?.options?.context
        || this.configuration?.options?.context
        || {};
    },
    questionnaireAppName() {
      return this.elementContext?.appName || DEFAULT_APP_NAME;
    },
    questionnaireApp() {
      try {
        return App.get(this.questionnaireAppName);
      } catch {
        return null;
      }
    },
    answerByQuestionId() {
      if (this.editMode || !this.submissionEntityId || !this.questionnaireApp) {
        return {};
      }
      const items = this.questionnaireApp.getFeedItems(
        SUBMISSION_ENTITY_TYPE,
        { id: this.submissionEntityId },
        ANSWERS_FIELD,
      ) ?? [];
      const byQuestionId = {};
      items.forEach(item => {
        const entityPk = item?.entityPk ?? item?.pk ?? null;
        if (!entityPk) {
          return;
        }
        const question = this.answerField(entityPk, QUESTION_FIELD);
        const questionId = Number(question?.pk?.id ?? question?.id ?? 0);
        if (!questionId) {
          return;
        }
        byQuestionId[questionId] = {
          entityPk,
          isAnswered: this.answerField(entityPk, IS_ANSWERED_FIELD) === true,
        };
      });
      return byQuestionId;
    },
    rows() {
      if (this.editMode || !this.questionnaireEntityId || !this.questionnaireApp) {
        return [];
      }
      const items = this.questionnaireApp.getFeedItems(
        QUESTIONNAIRE_ENTITY_TYPE,
        { id: this.questionnaireEntityId },
        QUESTIONS_FIELD,
      ) ?? [];
      return items
          .map(item => {
            const entityPk = item?.entityPk ?? item?.pk ?? null;
            if (!entityPk) {
              return null;
            }
            const answer = this.answerByQuestionId[entityPk.id] ?? null;
            const answerPk = this.myAnswerPk(entityPk) ?? answer?.entityPk ?? null;
            return {
              id: entityPk.id,
              entityPk,
              answerPk,
              isAnswered: this.isAnswerAnswered(answerPk, answer),
              uxElement: this.questionField(entityPk, UX_ELEMENT_FIELD),
              prompt: String(this.questionField(entityPk, PROMPT_FIELD) ?? "").trim(),
              sectionTitle: memberSectionTitle(this.questionField(entityPk, SECTION_NAME_PATH)),
              sectionId: this.sectionIdOf(entityPk),
              sectionRank: Number(this.questionField(entityPk, SECTION_RANK_PATH)) || 0,
              rank: Number(this.questionField(entityPk, RANK_FIELD)) || 0,
              required: this.questionField(entityPk, REQUIRED_FIELD) === true,
            };
          })
          .filter(row => row && row.id && row.uxElement && row.prompt)
          .sort((left, right) => left.sectionRank - right.sectionRank
            || left.sectionId - right.sectionId
            || left.rank - right.rank
            || left.id - right.id)
          .map((row, index, all) => ({
            ...row,
            startsSection: index === 0
              ? Boolean(row.sectionTitle)
              : row.sectionId !== all[index - 1].sectionId,
          }));
    },
    submissionCardId() {
      return Number(this.optionValueForKey(SUBMISSION_CARD_KEY)) || 0;
    },
    stepProgressCardId() {
      return Number(this.optionValueForKey(STEP_PROGRESS_CARD_KEY)) || 0;
    },
    stepNavCardId() {
      return Number(this.optionValueForKey(STEP_NAV_CARD_KEY)) || 0;
    },
    steps() {
      const steps = [];
      this.rows.forEach(row => {
        if (row.startsSection || steps.length === 0) {
          steps.push([]);
        }
        steps[steps.length - 1].push(row);
      });
      return steps;
    },
    showsOneSection() {
      return this.optionValueForKey(PACING_KEY) === PACING_SECTION
        && !this.isSubmitted
        && this.steps.length > 1;
    },
    stepCount() {
      return this.steps.length;
    },
    currentStep() {
      const stored = Number(this.submissionField({ id: this.submissionEntityId }, CURRENT_STEP_FIELD)) || 1;
      return Math.min(Math.max(stored - 1, 0), Math.max(this.stepCount - 1, 0));
    },
    stepNumber() {
      return this.currentStep + 1;
    },
    stepPercent() {
      if (this.stepCount === 0) {
        return 0;
      }
      return Math.round((this.currentStep + this.percentThroughThisStep) / this.stepCount * 100);
    },
    percentThroughThisStep() {
      if (this.visibleRows.length === 0) {
        return 1;
      }
      return this.visibleRows.filter(row => row.isAnswered).length / this.visibleRows.length;
    },
    isFirstStep() {
      return this.currentStep === 0;
    },
    isLastStep() {
      return this.currentStep >= this.stepCount - 1;
    },
    outstandingOnThisStep() {
      return this.visibleRows.filter(row => row.required && !row.isAnswered).length;
    },
    canLeaveThisStep() {
      return this.outstandingOnThisStep === 0;
    },
    currentSectionTitle() {
      if (!this.submissionEntityId) {
        return "";
      }
      return bandSectionTitle(this.visibleRows);
    },
    bandSectionId() {
      return this.visibleRows[0]?.sectionId ?? 0;
    },
    stepState() {
      return {
        stepCount: this.stepCount,
        stepNumber: this.stepNumber,
        stepPercent: this.stepPercent,
        currentSectionTitle: this.currentSectionTitle,
        stepOutstanding: this.outstandingOnThisStep,
        canLeaveStep: this.canLeaveThisStep,
        onFirstStep: this.isFirstStep,
        onLastStep: this.isLastStep,
        answeredCount: this.answeredCount,
      };
    },
    answeredCount() {
      return this.rows.filter(row => row.isAnswered).length;
    },
    visibleRows() {
      if (!this.showsOneSection) {
        return this.rows;
      }
      return this.steps[this.currentStep] ?? [];
    },
    showsSubmissionCard() {
      if (!this.submissionCardId || !this.submissionEntityId) {
        return false;
      }
      return !this.showsOneSection || this.isLastStep;
    },
    submittedCardId() {
      return Number(this.optionValueForKey(SUBMITTED_CARD_KEY)) || 0;
    },
    isSubmitted() {
      if (this.editMode || !this.submissionEntityId || !this.questionnaireApp) {
        return false;
      }
      const state = this.questionnaireApp.getEntityField(
        SUBMISSION_ENTITY_TYPE,
        { id: this.submissionEntityId },
        SUBMISSION_STATE_FIELD,
      );
      return state === SUBMISSION_STATE_COMPLETED;
    },
    submissionContexts() {
      return {
        [SUBMISSION_CONTEXT_NAME]: {
          entityType: SUBMISSION_ENTITY_TYPE,
          pk: { id: this.submissionEntityId },
        },
      };
    },
    boundPreviewRows() {
      const declarations = declarationsForApp(QUIZZES_POLLS_APP_NAME);
      return questionCardOptionKeys()
          .map((optionKey, index) => ({
            optionKey,
            label: declarations[optionKey]?.label ?? optionKey,
            componentId: Number(this.optionValueForKey(optionKey)) || 0,
            previewEntityId: index + 1,
          }))
          .filter(row => row.componentId > 0);
    },
    configurationProblems() {
      if (!this.questionnaireEntityId && !resolveNamedContextEntityId(this, SUBMISSION_CONTEXT_NAME)) {
        return "Pick a questionnaire in the inspector, or provide one as page context.";
      }
      const unbound = this.rows
          .map(row => row.uxElement)
          .filter(ux => !this.cardIdForUx(ux));
      if (unbound.length === 0) {
        return "";
      }
      return `No component is bound for the ${[...new Set(unbound)].join(", ")} answer input type(s).`;
    },
  },
  watch: {
    stepState: {
      immediate: true,
      handler() {
        this.publishStepState();
      },
    },
    questionnaireEntityId: {
      immediate: true,
      handler(questionnaireId) {
        this.loadQuestions(questionnaireId);
        this.loadOwnSubmissions();
      },
    },
    participantPk: {
      immediate: true,
      handler() {
        this.loadOwnSubmissions();
      },
    },
    resolvedSubmissionEntityId: {
      handler() {
        this.loadOwnSubmissions();
      },
    },
    submissionEntityId: {
      immediate: true,
      handler(submissionId) {
        if (this.editMode || !submissionId || !this.questionnaireApp) {
          return;
        }
        const pk = { id: submissionId };
        this.primeSubmissionFields(pk);
        this.questionnaireApp.triggerFeedInitialLoad(SUBMISSION_ENTITY_TYPE, pk, ANSWERS_FIELD);
        this.publishStepState();
      },
    },
  },
  mounted() {
    this.publishStepState();
  },
  updated() {
    this.publishStepState();
  },
  methods: {
    primeSubmissionFields(pk) {
      try {
        this.questionnaireApp.getEntityField(SUBMISSION_ENTITY_TYPE, pk, "submissionState");
        this.questionnaireApp.getEntityField(SUBMISSION_ENTITY_TYPE, pk, "participant");
        return true;
      } catch {
        return false;
      }
    },
    loadQuestions(questionnaireId) {
      if (this.editMode || !questionnaireId || !this.questionnaireApp) {
        return;
      }
      this.questionnaireApp.triggerFeedInitialLoad(
        QUESTIONNAIRE_ENTITY_TYPE,
        { id: questionnaireId },
        QUESTIONS_FIELD,
      );
    },
    loadOwnSubmissions() {
      if (this.editMode || !this.questionnaireEntityId || !this.participantPk || !this.questionnaireApp) {
        return;
      }
      this.questionnaireApp.triggerFeedInitialLoad(
        PARTICIPANT_ENTITY_TYPE,
        this.participantPk,
        SUBMISSIONS_FIELD,
      );
      if (this.resolvedSubmissionEntityId || this.isStartingSubmission) {
        return;
      }
      const meta = this.questionnaireApp.getFeedMeta(
        PARTICIPANT_ENTITY_TYPE,
        this.participantPk,
        SUBMISSIONS_FIELD,
        "hasLoadedAtLeastOnce",
      );
      if (!meta) {
        return;
      }
      this.isStartingSubmission = true;
      this.questionnaireApp.triggerEvent(START_SUBMISSION_EVENT, {
        questionnaire: { id: this.questionnaireEntityId },
      });
    },
    submissionField(entityPk, fieldName) {
      try {
        return this.questionnaireApp.getEntityField(SUBMISSION_ENTITY_TYPE, entityPk, fieldName);
      } catch {
        return undefined;
      }
    },
    answerField(entityPk, fieldName) {
      try {
        return this.questionnaireApp.getEntityField(ANSWER_ENTITY_TYPE, entityPk, fieldName);
      } catch {
        return undefined;
      }
    },
    questionnaireField(entityPk, fieldName) {
      try {
        return this.questionnaireApp.getEntityField(QUESTIONNAIRE_ENTITY_TYPE, entityPk, fieldName);
      } catch {
        return undefined;
      }
    },
    publishQuestionnaireSectionTitle() {
      if (this.editMode || !this.questionnaireEntityId || !this.questionnaireApp) {
        return;
      }
      const pk = { id: this.questionnaireEntityId };
      const changed = {};
      if (this.questionnaireField(pk, SECTION_TITLE_FIELD_ON_QUESTIONNAIRE) !== this.currentSectionTitle) {
        changed[SECTION_TITLE_FIELD_ON_QUESTIONNAIRE] = this.currentSectionTitle;
      }
      if (Object.keys(changed).length === 0) {
        return;
      }
      this.questionnaireApp.triggerEventWithoutSendingToServer(UPDATE_FIELD_EVENT, {
        entityType: QUESTIONNAIRE_ENTITY_TYPE,
        entityPk: pk,
        fields: changed,
      });
    },
    myAnswerPk(questionPk) {
      const mine = this.questionField(questionPk, MY_ANSWER_FIELD);
      const id = Number(mine?.pk?.id ?? mine?.id ?? 0);
      return id ? { id } : null;
    },
    isAnswerAnswered(answerPk, feedAnswer) {
      if (answerPk) {
        return this.answerField(answerPk, IS_ANSWERED_FIELD) === true;
      }
      return feedAnswer?.isAnswered === true;
    },
    cardIdForUx(ux) {
      return Number(this.optionValueForKey(cardOptionKeyForUx(ux))) || 0;
    },
    optionValueForKey(optionKey) {
      const optionClass = declaredAppOption(QUIZZES_POLLS_APP_NAME, optionKey);
      return optionClass ? this.getOptionValue(optionClass) : 0;
    },
    publishStepState() {
      this.publishQuestionnaireSectionTitle();
      if (this.editMode || !this.submissionEntityId || !this.questionnaireApp) {
        return;
      }
      const pk = { id: this.submissionEntityId };
      const changed = {};
      STEP_STATE_FIELDS.forEach(fieldName => {
        if (this.submissionField(pk, fieldName) !== this.stepState[fieldName]) {
          changed[fieldName] = this.stepState[fieldName];
        }
      });
      if (Object.keys(changed).length === 0) {
        return;
      }
      this.questionnaireApp.triggerEventWithoutSendingToServer(UPDATE_FIELD_EVENT, {
        entityType: SUBMISSION_ENTITY_TYPE,
        entityPk: pk,
        fields: changed,
      });
    },
    cardIdForRow(row) {
      if (this.isSubmitted && this.submittedCardId) {
        return this.submittedCardId;
      }
      return this.cardIdForUx(row?.uxElement);
    },
    rowContexts(row) {
      const contexts = {
        [QUESTION_CONTEXT_NAME]: {
          entityType: QUESTION_ENTITY_TYPE,
          pk: row.entityPk,
        },
        [SUBMISSION_CONTEXT_NAME]: {
          entityType: SUBMISSION_ENTITY_TYPE,
          pk: { id: this.submissionEntityId },
        },
      };
      if (row.answerPk) {
        contexts[ANSWER_CONTEXT_NAME] = {
          entityType: ANSWER_ENTITY_TYPE,
          pk: row.answerPk,
        };
      }
      return contexts;
    },
    sectionIdOf(questionPk) {
      const section = this.questionField(questionPk, SECTION_FIELD);
      return Number(section?.pk?.id ?? section?.id ?? 0);
    },
    questionField(entityPk, fieldName) {
      try {
        return this.questionnaireApp.getEntityField(QUESTION_ENTITY_TYPE, entityPk, fieldName);
      } catch {
        return undefined;
      }
    },
  },
};
</script>
